import React, { useState, useEffect, Fragment } from "react";
import {
  Table,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  FormFeedback,
  Form,
  Input
} from "reactstrap";
import * as Yup from "yup";
import { useFormik } from "formik";
import Select from "react-select";
import { useSelector, useDispatch } from "react-redux";
import { map } from "lodash";
import "flatpickr/dist/themes/material_blue.css";
import BreadcrumbLink from "../../components/Common/BreadcrumbLink";
import i18n from "../../i18n";
import { listStatusHistorical, listType, listPlaces } from "../../common/data";
import Spinners from "../../components/Common/Spinner";
import CardService from "./CardService";
import PaginationV2 from "../../components/Pagination/PaginationV2";
import RequestServiceCancelModal from "./RequestServiceCancelModal";
import { WebsocketService } from "./WebsocketService";
import { getOrgsAdmin, getEmployeeBookings } from "../../helpers/api_rombo_helper";
import AlertSuccess from "../../components/Alerts/AlertSuccess";
import { createSelector } from "reselect";
import { FilterType, StreamType, TypeService, Permissions, DataActions, UserType, ServiceStatus } from "../../constants/enums";
import { orgId, handleSearchFilters } from "../../constants/utils";
import folderNoResults from "../../assets/images/questionmark-folder.png";

const HistoricalServices = (props) => {
  document.title = `${i18n.t("Historical")} - ${orgId}`;

  const dispatch = useDispatch();

  const [isLoading, setLoading] = useState(true);
  const [findServices, setFindServices] = useState([]);
  const [listServices, setListServices] = useState({ page: 1, size: 10, pages: 1, items: [] });
  const [infoUser, setInfoUser] = useState({ permissions: [] });
  const [requestServiceCancelModal, setRequestServiceCancelModal] = useState(false);
  const toggleViewModalCancelService = () => setRequestServiceCancelModal(!requestServiceCancelModal);
  const [showNoResults, setShowNoResults] = useState(false);
  const [showAlertSuccess, setShowAlertSuccess] = useState(false);
  const [clickAssignService, setClickAssignService] = useState(false);
  const [textHeader, setTextHeader] = useState('');
  const [textBody, setTextBody] = useState('');
  const [listCompanies, setListCompanies] = useState([]);

  const savedFilters = JSON.parse(localStorage.getItem("historicalFilters")) || { data: { filters: {} } };

  const [fCompany, setFCompany] = useState(savedFilters.data?.filters?.partner_id || null);
  const [typeService, setTypeService] = useState(savedFilters.data?.filters?.type || null);
  const [statusService, setStatusService] = useState(savedFilters.data?.filters?.status || null);
  const [selectedService, setSelectedService] = useState({});
  const [reloadList, setReloadList] = useState(true);
  const [serviceNumber, setServiceNumber] = useState(savedFilters.data?.filters?.code || null);
  const [placeService, setPlaceService] = useState(savedFilters.data?.filters?.place_id || null);
  const [plateService, setPlateService] = useState(savedFilters.data?.filters?.plate || null);

  const [currentPage, setCurrentPage] = useState(savedFilters.data?.filters?.page || 1);
  const [recordsPerPage] = useState(7);
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const [currentRecords, setCurrentRecords] = useState(findServices.slice(indexOfFirstRecord, indexOfLastRecord));
  const nPages = Math.ceil(findServices.length / recordsPerPage);

const [filtersOpts, setFiltersOpts] = useState({
    eventType: "sendMessage",
    metadata: { platform: "WEB", screen: "HISTORY" },
    data: { action: DataActions.GET_BOOKINGS, filters: { page: 1, size: 10 } }
  });
  
  const autoformik = useFormik({
    initialValues: {
      search: "",
      serviceNumber: "",
      plate: "",
      status: "",
      type: "",
      place_id: "",
    },
    validationSchema: Yup.object({
      search: Yup.string(),
      serviceNumber: Yup.string(),
      plate: Yup.string(),
      status: Yup.string(),
      type: Yup.string(),
      place_id: Yup.string(),
    })
  });

  const LoginProperties = createSelector(
    (state) => state.Login,
    (Login) => ({
      token: Login.token,
      loading: Login.loading,
      data: Login.data
    })
  );

  const { token, loading, data } = useSelector(LoginProperties);

  const isEmployee = infoUser.roles === UserType.EMPLOYEE;

  const fetchEmployeeBookings = async () => {
    try {
      const response = await getEmployeeBookings(token, {
        page: 1,
        size: 10,
        status: [ServiceStatus.FINISHED, ServiceStatus.DELETED], 
      });
      setListServices({
        page: response.page,
        size: response.size,
        pages: response.pages,
        items: response.items,
      });
      setFindServices(response.items);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching employee bookings:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isEmployee) {
      fetchEmployeeBookings();
    } else {
      const savedFilters = localStorage.getItem("historicalFilters");
      if (savedFilters) {
        const parsedFilters = JSON.parse(savedFilters);
        setFiltersOpts(parsedFilters);
        setCurrentPage(parsedFilters.data.filters.page || 1);
      }
    }
  }, [isEmployee]);

  useEffect(() => {
    if (data?.data) {
      setInfoUser(data.data);
      
      if (!data.data.is_staff) {
        searchFilters(data.data.partner_id, "company");
      }
    }
  }, [data, loading]);

  useEffect(() => {
    setFindServices(listServices.items);
  }, [listServices]);

  const searchFilters = (val, typeSearch) => {
    handleSearchFilters(
      val,
      typeSearch,
      filtersOpts,
      setFiltersOpts,
      setReloadList,
      setCurrentPage,
      "historicalFilters",
      {
        setFCompany,
        setStatusService,
        setTypeService,
        setServiceNumber,
        setPlaceService,
        setPlateService,
      }
    );
  };

  const onGetOrgsAdmin = () => {
    let arr = [];
    getOrgsAdmin(token)
      .then((response) => {
        response.forEach((item) => {
          arr.push({ label: item.name, value: item.partner_id });
        });
        setListCompanies([...arr]);
      })
      .catch((error) => {
        console.error("Error - onGetOrgsAdmin - ", error.response?.data || error.message);
        setErrorAlert(true);
        setErrorAlertMessage((error?.response?.data?.detail || error.message));
      });
  };

useEffect(() => {
  onGetOrgsAdmin();
  if (localStorage.getItem("notify") === "true") {
    setShowAlertSuccess(true);
    setTextHeader(localStorage.getItem("headerText"));
    setTextBody(localStorage.getItem("bodyText"));
    localStorage.setItem("notify", "false");
  }
}, []);

useEffect(() => {
  if (isLoading || findServices.length > 0) {
    setShowNoResults(false);
  }

  if (!isLoading && findServices.length === 0) {
    const timer = setTimeout(() => {
      setShowNoResults(true);
    }, 2000);

    return () => clearTimeout(timer);
  }
}, [isLoading, findServices]);

  return (
    <Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <BreadcrumbLink
            title={i18n.t("Services")}
            link='/services/reservations'
            breadcrumbItem={i18n.t("Historical")}
          />
          <Row>
            <Card>
              <CardBody>
                <Row className="pb-2">
                  <Col xxl={9} md={7} xs={12}>
                    <CardTitle className="h4">{i18n.t("Historical")}</CardTitle>
                    {!isLoading && (
                      <>
                        {(!infoUser.permissions.includes(Permissions.SERVICES_READ)) ? (
                          <span className="error-message-requests">
                            <i className="fa fa-exclamation-circle"></i>
                            {i18n.t("NoPermissionsViewReservations")}
                          </span>
                        ) : (
                          listServices.items.length === 0 && showNoResults && (
                            <span className="error-message-requests">
                              <i className="fa fa-exclamation-circle"></i>
                              {i18n.t("ServiceRequestsCouldNotBeLoaded")}
                            </span>
                          )
                        )}
                      </>
                    )}
                  </Col>
                </Row>
                <Row className="pb-2">
                <div className="orange-separator" />
                  <Form className="row gx-3" onSubmit={autoformik.handleSubmit}>
                  {infoUser.is_staff && (
                    <Col xxl={2} md={2} xs={12}>
                      <Card className="mb-2">
                        <Select
                          name="company"
                          placeholder={i18n.t("SelectPartnerName")}
                          options={listCompanies}
                          isClearable
                          className="select2-selection"
                          value={listCompanies.find((item) => item.value === fCompany) || null}
                          onChange={(e) => {
                            searchFilters(e?.value || null, FilterType.COMPANY);
                          }}
                        />
                      </Card>
                    </Col>
                  )}
                    <Col xxl={2} md={2} xs={12} className="mb-2">
                      <div className="col-sm-auto">
                        <Input
                          type="text"
                          name="serviceNumber"
                          className="form-control"
                          id="serviceNumberInput"
                          placeholder={i18n.t("ID")}
                          value={serviceNumber}
                          onChange={(e) => {
                            const value = e.target.value.trim();
                            setServiceNumber(value);
                            searchFilters(value || null, FilterType.CODE);
                          }}
                          onBlur={autoformik.handleBlur}
                          invalid={
                            autoformik.touched.serviceNumber && autoformik.errors.serviceNumber
                              ? true
                              : false
                          }
                        />
                        {autoformik.errors.serviceNumber && autoformik.touched.serviceNumber ? (
                          <FormFeedback type="invalid">
                            {autoformik.errors.serviceNumber}
                          </FormFeedback>
                        ) : null}
                      </div>
                    </Col>
                    <Col xxl={2} md={2} xs={12} className="mb-2">
                      <div className="col-sm-auto">
                        <Input
                          type="text"
                          name="plateService"
                          className="form-control"
                          id="plateServiceInput"
                          placeholder={i18n.t("Plate")}
                          value={plateService}
                          onChange={(e) => {
                            const value = e.target.value.trim();
                            setPlateService(value);
                            searchFilters(value || null, FilterType.PLATE);
                          }}
                          onBlur={autoformik.handleBlur}
                          invalid={
                            autoformik.touched.plateService && autoformik.errors.plateService
                              ? true
                              : false
                          }
                        />
                        {autoformik.errors.plateService && autoformik.touched.plateService ? (
                          <FormFeedback type="invalid">
                            {autoformik.errors.plateService}
                          </FormFeedback>
                        ) : null}
                      </div>
                    </Col>
                    <Col xxl={2} md={2} xs={12}>
                      <Card className="mb-2">
                        <Select
                          name="status"
                          placeholder={i18n.t("Status")}
                          options={listStatusHistorical()}
                          isClearable
                          className="select2-selection"
                          value={listStatusHistorical().find((item) => item.value === statusService) || null}
                          onChange={(e) => {
                            searchFilters(e?.value || null, FilterType.STATUS);
                          }}
                        />
                      </Card>
                    </Col>
                    <Col xxl={2} md={2} xs={12}>
                      <Card className="mb-2">
                        <Select
                          name="type"
                          placeholder={i18n.t("Type")}
                          options={listType()}
                          isClearable
                          className="select2-selection"
                          value={listType().find((item) => item.value === typeService) || null}
                          onChange={(e) => {
                            searchFilters(e?.value || null, FilterType.TYPE);
                          }}
                        />
                      </Card>
                    </Col>
                    <Col xxl={2} md={2} xs={12} className="mb-2">
                      <div className="col-sm-auto">
                        <Select
                          name="place"
                          placeholder={i18n.t("Place")}
                          options={listPlaces()}
                          isClearable
                          className="select2-selection"
                          value={listPlaces().find((item) => item.value === placeService) || null}
                          onChange={(e) => {
                            searchFilters(e?.value || null, FilterType.PLACE);
                          }}
                        />
                        {autoformik.errors.placeService && autoformik.touched.placeService ? (
                          <FormFeedback type="invalid">
                            {autoformik.errors.placeService}
                          </FormFeedback>
                        ) : null}
                      </div>
                    </Col>
                  </Form>
                  <div className="orange-separator" />
                </Row>
                <div className="table-responsive pt-3">
                  <Table className="table table-bordered mb-0 table-request-services">
                  {infoUser.permissions.includes(Permissions.SERVICES_READ) && (
                    <thead>
                      <tr>
                        <th className="company-column">{i18n.t("Company")}</th>
                        <th className="request-number-column">{i18n.t("ID")}</th>
                        <th className="plate-column">{i18n.t("Plate")}</th>
                        <th className="origin-column">{i18n.t("Origin")}</th>
                        <th className="destination-column">{i18n.t("Destination")}</th>
                        <th className="date-column">{i18n.t("Date")}</th>
                        <th className="hour-column">{i18n.t("Hour")}</th>
                        <th className="type-column">{i18n.t("Type")}</th>
                        <th className="type-column">{i18n.t("Place")}</th>
                        <th className="status-column">{i18n.t("Status")}</th>
                        <th className="actions-header">{i18n.t("Actions")}</th>
                      </tr>
                    </thead>
                  )}
                    {!isLoading && !infoUser.permissions.includes(Permissions.SERVICES_READ) ? (
                      <tbody>
                        <tr>
                          <td colSpan="9" className="text-center">
                            <img
                              src={folderNoResults}
                              alt="No results"
                              className="img-no-results"
                            />
                          </td>
                        </tr>
                      </tbody>
                    ) : (
                      (!isLoading && findServices.length === 0 ? (
                        <tbody>
                          <tr>
                            <td colSpan="9" className="text-center">
                              {showNoResults && (
                                <img
                                  src={folderNoResults}
                                  alt="No results"
                                  className="img-no-results"
                                />
                              )}
                            </td>
                          </tr>
                        </tbody>
                      ) : (
                        <tbody>
                          {map(findServices, (service, key) => (
                            <CardService
                              service={service}
                              infoUser={infoUser}
                              setSelectedService={setSelectedService}
                              typeService={TypeService.RESERVATIONS}
                              setRequestServiceCancelModal={setRequestServiceCancelModal}
                              key={"_shop_" + key}
                              clickAssignService={clickAssignService}
                            />
                          ))}
                        </tbody>
                      ))
                    )}
                  </Table>
                  {!isEmployee && (
                    <WebsocketService
                      setLoading={setLoading}
                      filtersOpts={filtersOpts}
                      uuid={infoUser.external_id}
                      listServices={listServices}
                      setListServices={setListServices}
                      reloadList={reloadList}
                      allowedActions={[
                        StreamType.BOOKING_CREATED,
                        StreamType.BOOKING_UPDATED,
                        StreamType.BOOKING_DELETED
                      ]}
                    />
                  )}
                  <RequestServiceCancelModal
                    token={token}
                    setReloadList={setReloadList}
                    reloadList={reloadList}
                    typeService={TypeService.RESERVATIONS}
                    isOpen={requestServiceCancelModal}
                    toggle={toggleViewModalCancelService}
                    setRequestServiceCancelModal={setRequestServiceCancelModal}
                    setShowAlertSuccess={setShowAlertSuccess}
                    setTextHeader={setTextHeader}
                    setTextBody={setTextBody}
                    setSelectedService={setSelectedService}
                    selectedService={selectedService}
                  />
                  {isLoading ? (
                    <Spinners setLoading={setLoading} />
                  ) : (
                    infoUser.permissions.includes(Permissions.SERVICES_READ) && (
                      <PaginationV2
                        currentPage={currentPage}
                        pageSize={listServices.size}
                        totalItems={listServices.total}
                        onChangePage={(page) => {
                          setCurrentPage(page);
                          searchFilters(page, "page");
                        }}
                      />
                    )
                  )}
                </div>
              </CardBody>
            </Card>
          </Row>
          <AlertSuccess
            showAlertSuccess={showAlertSuccess}
            setShowAlertSuccess={setShowAlertSuccess}
            textHeader={textHeader}
            textBody={textBody}
          />
        </div>
      </div>
    </Fragment>
  );
};

export default HistoricalServices;
